import { parseToDateTime } from '@calendly/datetime';
import { I18n } from '@calendly/i18n';
import { BodyText, LabelText, TitleText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { forwardRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGetUserQuery } from 'common';
import { getRules } from 'components/availability/utils';
import { timeNotationSelector } from 'selectors/current_user';
import { formatTime, parseTime, shortUserDateRangeFormatWithNoYear, userDateFormat } from 'services/time_format';

// Figma Available hours Weekdays: grid-cols-[130px_minmax(0,1fr)], gap 8px.
// Times column fills remaining width (282px in the design frame at card size).

const DAY_ORDER = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const sectionCss = "s18rvuwc";
const hoursListCss = "hwf3248";
const hoursRowCss = "h1197k76";
const Section = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(sectionCss, props.className)} />;
});
const formatIntervalRange = (interval, timeNotation) => {
  const from = formatTime(parseTime(interval.from, '24h'), timeNotation, timeNotation === '24h' ? undefined : 'h:mm a');
  const to = formatTime(parseTime(interval.to, '24h'), timeNotation, timeNotation === '24h' ? undefined : 'h:mm a');
  return `${from} – ${to}`;
};
const formatDateRangeLabel = dateRange => {
  const fromDate = parseToDateTime(dateRange[0]);
  const toDate = parseToDateTime(dateRange[dateRange.length - 1]);
  if (dateRange.length === 1) {
    return userDateFormat(fromDate, 'american', 'date_small');
  }
  const [rangeStart, rangeEnd] = shortUserDateRangeFormatWithNoYear(fromDate, toDate);
  return `${rangeStart} – ${rangeEnd}`;
};
const HoursRow = ({
  label,
  labelMuted = false,
  children
}) => <div className={hoursRowCss} data-testid="v4-hours-row">
    <LabelText color={labelMuted ? 'muted' : undefined} size="large">
      {label}
    </LabelText>
    <div>{children}</div>
  </div>;
export const V4DefaultHoursView = ({
  days = {}
}) => {
  const timeNotation = useSelector(timeNotationSelector);
  const abbrDayNames = I18n.t('date.abbr_day_names_hash');
  const orderedDays = useMemo(() => DAY_ORDER.filter(wday => wday in days), [days]);
  return <Section data-component="days-container" data-testid="v4-default-hours-view">
      <TitleText level={3} size="small">
        {I18n.t('publisher.event_type_editor.sections.available_hours.weekly_hours')}
      </TitleText>
      <div className={hoursListCss}>
        {orderedDays.map(wday => {
        const intervals = days[wday] || [];
        const isUnavailable = intervals.length === 0;
        return <HoursRow key={wday} label={abbrDayNames[wday]} labelMuted={isUnavailable}>
              {isUnavailable ? <BodyText color="muted" size="medium">
                  {I18n.t('publisher.event_types.availability.unavailable')}
                </BodyText> : intervals.map(({
            from,
            to
          }) => <BodyText key={`${from}-${to}`} size="medium">
                    {formatIntervalRange({
              from,
              to
            }, timeNotation)}
                  </BodyText>)}
            </HoursRow>;
      })}
      </div>
    </Section>;
};
export const V4DateSpecificReadOnlyView = ({
  dates = []
}) => {
  const {
    data: {
      time_notation: timeNotation
    } = {
      time_notation: '12h'
    }
  } = useGetUserQuery();
  const rules = useMemo(() => getRules(dates), [dates]);
  return <Section data-testid="v4-date-specific-read-only-view">
      <TitleText level={3} size="small">
        {I18n.t('publisher.event_type_editor.sections.available_hours.date_overrides.date_specific_hours')}
      </TitleText>
      {rules.length === 0 ? <BodyText color="muted" size="medium">
          {I18n.t('publisher.event_type_editor.sections.available_hours.date_overrides.none')}
        </BodyText> : <div className={hoursListCss}>
          {rules.map(({
        dates: dateRange,
        intervals
      }) => {
        const isUnavailable = intervals.length === 0;
        return <HoursRow key={dateRange.join('-')} label={formatDateRangeLabel(dateRange)}>
                {isUnavailable ? <BodyText color="muted" size="medium">
                    {I18n.t('publisher.event_type_editor.sections.scheduling_settings.date_overrides.unavailable')}
                  </BodyText> : <BodyText size="medium">
                    {intervals.map(({
              from,
              to
            }) => formatIntervalRange({
              from,
              to
            }, timeNotation)).join(', ')}
                  </BodyText>}
              </HoursRow>;
      })}
        </div>}
    </Section>;
};

require("./v4_default_hours_view.wyw-in-js.css!=!../../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./v4_default_hours_view.js");